import { HttpStatusCode, isAxiosError } from 'axios';
import { trim } from 'lodash';
import { ChangeEvent, MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Snackbar } from 'src/components/snackbar';
import { PATHS } from 'src/constants/paths';
import { useClone, useDeleteLocationGroup, useMenu, useModal } from 'src/hooks';
import { ILocationGroupTabList } from 'src/interfaces';
import { lang } from 'src/utils';

export const useLocationGroupRowActions = (row: ILocationGroupTabList) => {
  const navigate = useNavigate();
  const menu = useMenu();
  const deleteModal = useModal();
  const duplicateModal = useModal();
  const deleteLocationGroup = useDeleteLocationGroup();
  const cloneLocationGroup = useClone(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [duplicatedName, setDuplicatedName] = useState('');

  const onMenuClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    menu.onOpenMenu(event.currentTarget);
  };

  const onDuplicateClick = () => {
    duplicateModal.onOpenModal();
    menu.onCloseMenu();
  };

  const onDuplicateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDuplicatedName(event.target.value);
  };

  const onCloseDuplicateModal = () => {
    duplicateModal.onCloseModal();
    setDuplicatedName('');
  };

  const onConfirmDuplicate = async () => {
    setIsDuplicating(true);
    try {
      const newName = trim(duplicatedName);
      const narrative =
        lang.duplicateLocationGroupModal.getDuplicationNarrative(
          newName,
          row.name,
        );
      const response = await cloneLocationGroup.mutateAsync({
        type: 'location-group',
        id: row.id,
        name: newName,
        narrative,
      });
      Snackbar.success(
        lang.duplicateLocationGroupModal.getSuccessMessage(newName, row.name),
      );
      setDuplicatedName('');
      duplicateModal.onCloseModal();
      navigate(PATHS.LOCATION_GROUP_DETAILS(response.id.toString()));
    } catch (error: unknown) {
      if (
        isAxiosError(error) &&
        error.response?.status === HttpStatusCode.Conflict
      ) {
        Snackbar.error(
          error.response.data?.error?.replace('Error:', '') ??
            lang.duplicateLocationGroupModal.genericError,
        );

        return;
      }
      duplicateModal.onCloseModal();
      setDuplicatedName('');
      Snackbar.error(lang.duplicateLocationGroupModal.genericError);
    } finally {
      setIsDuplicating(false);
    }
  };

  const onDeleteClick = () => {
    deleteModal.onOpenModal();
  };

  const onConfirmDelete = async () => {
    deleteModal.onCloseModal();
    try {
      setIsDeleting(true);
      await deleteLocationGroup.mutateAsync({
        id: row.id.toString(),
        name: row.name,
      });
      Snackbar.success(`${row.name} ${lang.successfullyDeleted}`);
    } catch {
      Snackbar.error(lang.failedDeleteText);
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    menu,
    deleteModal,
    duplicateModal,
    isDeleting,
    isDuplicating,
    duplicatedName,
    onMenuClick,
    onDuplicateClick,
    onDuplicateChange,
    onCloseDuplicateModal,
    onConfirmDuplicate,
    onDeleteClick,
    onConfirmDelete,
  };
};
